import { ChangeDetectionStrategy, Component, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export interface Category {
  id: string;
  label: string;
  icon: string;
  prompts: number;
}

export interface MediaType {
  id: string;
  label: string;
  description: string;
  icon: string;
}

export interface Question {
  id: number;
  text: string;
  selected: boolean;
  answered: boolean;
}

@Component({
  selector: 'app-story-record',
  templateUrl: './story-record.html',
  styleUrl: './story-record.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
})
export class StoryRecord {
  private router = inject(Router);

  // ── Steps ────────────────────────────────────────────────────────────────
  readonly currentStep = signal<number>(1);
  readonly totalSteps = 4;

  goToStep(step: number): void {
    if (step >= 1 && step <= this.totalSteps) this.currentStep.set(step);
  }
  nextStep(): void { this.goToStep(this.currentStep() + 1); }
  prevStep(): void { this.goToStep(this.currentStep() - 1); }

  // ── Step 1: Categories ───────────────────────────────────────────────────
  readonly categories: Category[] = [
    { id: 'childhood',    label: 'Childhood',           icon: 'images/icons/memory-share-icons/fm-ms-icon1.svg', prompts: 4 },
    { id: 'family',       label: 'Family & Heritage',   icon: 'images/icons/memory-share-icons/fm-ms-icon2.svg', prompts: 4 },
    { id: 'love',         label: 'Love & Relationship', icon: 'images/icons/memory-share-icons/fm-ms-icon4.svg', prompts: 4 },
    { id: 'career',       label: 'Career & Work',       icon: 'images/icons/memory-share-icons/fm-ms-icon3.svg', prompts: 4 },
    { id: 'life-moment',  label: 'Major Life Moment',   icon: 'images/icons/memory-share-icons/fm-ms-icon5.svg', prompts: 4 },
    { id: 'challenges',   label: 'Challenge & Triumphs',icon: 'images/icons/memory-share-icons/fm-ms-icon6.svg', prompts: 4 },
    { id: 'wisdom',       label: 'Wisdom & Advice',     icon: 'images/icons/memory-share-icons/fm-ms-icon7.svg', prompts: 4 },
    { id: 'fun',          label: 'Fun Memories',        icon: 'images/icons/memory-share-icons/fm-ms-icon8.svg', prompts: 4 },
  ];
  readonly selectedCategory = signal<string>('childhood');
  selectCategory(id: string): void { this.selectedCategory.set(id); }

  // ── Step 2: Media Type ───────────────────────────────────────────────────
  readonly mediaTypes: MediaType[] = [
    { id: 'video',       label: 'Video Recording', description: 'Record or upload video to share your story',  icon: 'images/icons/memory-share-icons/fm-ts-icon1.svg' },
    { id: 'audio',       label: 'Audio Recording', description: 'Record audio to tell your story',             icon: 'images/icons/memory-share-icons/fm-ts-icon2.svg' },
    { id: 'image-audio', label: 'Image + Audio',   description: 'Upload photos and describe them with audio',  icon: 'images/icons/memory-share-icons/fm-ts-icon3.svg' },
  ];
  readonly selectedMedia = signal<string>('video');
  selectMedia(id: string): void { this.selectedMedia.set(id); }

  // ── Step 3: Questions ────────────────────────────────────────────────────
  readonly questions = signal<Question[]>([
    { id: 1, text: 'What was your favorite childhood game?',              selected: true,  answered: false },
    { id: 2, text: 'Who was your best friend growing up?',               selected: true,  answered: false },
    { id: 3, text: 'Share a memory about your grandparents',             selected: false, answered: false },
    { id: 4, text: 'Tell me about your wedding day',                     selected: false, answered: false },
    { id: 5, text: 'Share a romantic memory that still makes you smile',  selected: false, answered: false },
  ]);
  readonly selectedQuestions = computed(() => this.questions().filter(q => q.selected));

  toggleQuestion(id: number): void {
    this.questions.update(list =>
      list.map(q => q.id === id ? { ...q, selected: !q.selected } : q)
    );
  }

  deleteQuestion(id: number): void {
    this.questions.update(list => list.filter(q => q.id !== id));
  }

  readonly showCustomQuestion = signal(false);
  readonly customQuestionText = signal('');
  toggleCustomQuestion(): void { this.showCustomQuestion.update(v => !v); }

  saveCustomQuestion(): void {
    const text = this.customQuestionText().trim();
    if (!text) return;
    const nextId = Math.max(...this.questions().map(q => q.id), 0) + 1;
    this.questions.update(list => [...list, { id: nextId, text, selected: true, answered: false }]);
    this.customQuestionText.set('');
    this.showCustomQuestion.set(false);
  }

  // ── Step 4: Recording ────────────────────────────────────────────────────
  readonly currentQuestionIndex = signal(0);
  readonly currentQuestion = computed<Question | undefined>(() => this.selectedQuestions()[this.currentQuestionIndex()]);
  readonly isLastQuestion = computed(() =>
    this.currentQuestionIndex() >= this.selectedQuestions().length - 1
  );
  readonly progressPercent = computed(() => {
    const total = this.selectedQuestions().length;
    return total > 0 ? Math.round(((this.currentQuestionIndex() + 1) / total) * 100) : 0;
  });

  nextQuestion(): void {
    if (!this.isLastQuestion()) this.currentQuestionIndex.update(i => i + 1);
  }

  finishAndReview(): void { this.router.navigate(['/app/stories']); }
  cancelRecord(): void    { this.router.navigate(['/app/stories']); }
}
